import { useMemo, useState } from 'react'
import type { ProjectSummary } from '../lib/db'
import { money } from '../lib/paper'
import {
  BILL_STATES,
  billState,
  byUrgency,
  depositsOut,
  isInvoice,
  late,
  NOT_TRACKED,
  owed,
  said,
  type BillRef,
  type BillState,
} from '../lib/invoices'
import { Empty, Loading, NotKnown } from './bits'
import { IconChevron, IconMore } from './icons'

/**
 * WHAT IS OWED, AND WHAT IS LATE.
 *
 * PAY-01. Money was a column on a job row, which is the wrong shape for the
 * only question this screen exists to answer. A column can be sorted; it
 * cannot be totalled, filtered by lateness, or read on a phone as a list of
 * four names to ring this morning.
 *
 * ORDERED BY URGENCY, NOT BY DATE. Late first with the latest at the top, then
 * owed by soonest due, then the ones nobody has issued, then what settled
 * recently. Same shape as the estimates list, because it is the same kind of
 * question asked about the other half of the money.
 *
 * NOTHING HERE COLLECTS. Payments in this trade are handed over outside the
 * app, so this reads the list of what a person recorded as arrived. It never
 * charges anybody and never marks a thing settled that nobody entered.
 */
export default function Invoices({
  items,
  onOpenJob,
}: {
  items: ProjectSummary[] | null
  onOpenJob: (job: ProjectSummary) => void
}) {
  const [filter, setFilter] = useState<BillState | 'all'>('all')
  const [openId, setOpenId] = useState<string | null>(null)
  const today = new Date().toISOString().slice(0, 10)

  const jobs = useMemo(() => items ?? [], [items])
  const bills = useMemo<ProjectSummary[]>(() => jobs.filter((j) => isInvoice(j)), [jobs])

  const rows = useMemo(() => {
    const sorted = byUrgency(bills as BillRef[], today) as ProjectSummary[]
    return filter === 'all' ? sorted : sorted.filter((j) => billState(j, today) === filter)
  }, [bills, filter, today])

  const out = owed(bills as BillRef[], today)
  const overdue = late(bills as BillRef[], today)
  const deposits = depositsOut(bills as BillRef[], today)
  const n = bills.filter((j) => {
    const s = billState(j, today)
    return s === 'due' || s === 'overdue'
  }).length

  const who = (j: ProjectSummary) => j.customer.trim() || j.address.trim() || j.name

  const tone = (s: BillState) =>
    s === 'overdue' ? 'owed' : s === 'paid' ? 'paid' : s === 'due' ? 'waiting' : ''

  if (items === null) return <Loading what="Reading your jobs" />

  return (
    <>
      <div className="ws-head">
        <div>
          <h1>Invoices</h1>
          <p>What is owed, latest first. Money is recorded here, never collected here.</p>
        </div>
      </div>

      {/*
        THE ANSWER, IN ONE SENTENCE, and the second half of it is the one that
        matters: a total owed is a business, a total late is a morning.
      */}
      {bills.length > 0 && (
        <p className="est-answer">
          {out === 0 ? (
            <>Nothing is owed on an issued invoice right now.</>
          ) : (
            <>
              <b>{money(out)}</b> is owed across {n} {n === 1 ? 'invoice' : 'invoices'}.
              {overdue > 0 && <> {money(overdue)} of it is past due.</>}
              {deposits > 0 && <> {money(deposits)} of deposits has not arrived.</>}
            </>
          )}
        </p>
      )}

      <div className="ws-tools">
        <div className="ws-tabs">
          <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </button>
          {BILL_STATES.map((s) => (
            <button key={s.id} aria-pressed={filter === s.id} onClick={() => setFilter(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="ws-card">
          {bills.length === 0 ? (
            <Empty title="No invoices yet">
              A quote becomes one from the quote sheet, on the job.
            </Empty>
          ) : (
            <Empty
              kind="filtered"
              action={{ label: 'Show every invoice', onClick: () => setFilter('all') }}
            >
              Nothing matches that.
            </Empty>
          )}
        </div>
      ) : (

        <div className="ws-card est-list">
          <div className="ws-thead" aria-hidden="true">
            <span />
            <span>Invoice</span>
            <span>State</span>
            <span>Owed</span>
            <span />
          </div>
          {rows.map((j) => {
            const state = billState(j, today)
            const m = j.money
            return (
              <div key={j.id} className="ws-item">
                <div className="ws-row">
                  <button
                    className="ws-open"
                    onClick={() => onOpenJob(j)}
                    aria-label={`Open ${who(j)}`}
                  >
                    <span className="ws-thumb">{j.thumb ? <img src={j.thumb} alt="" /> : <i />}</span>
                    <span className="ws-name">
                      <strong>
                        {who(j)}
                        {m?.number && <em className="est-no">{m.number}</em>}
                      </strong>
                      <span>{said(j, today)}</span>
                    </span>
                    <span className={`ws-pill ${tone(state)}`}>
                      {BILL_STATES.find((s) => s.id === state)?.short}
                    </span>
                    {/*
                      THE OWED COLUMN SAYS WHAT IS OWED, not what the job came
                      to. On a list about money outstanding, printing the total
                      in the loud position and the balance underneath it puts
                      the number nobody is chasing on top.
                    */}
                    <span className="ws-amount">
                      {state === 'paid' ? money(m?.total ?? 0) : money(m?.balance ?? 0)}
                      {state !== 'paid' && (m?.paid ?? 0) > 0 && (
                        <em>of {money(m?.total ?? 0)}</em>
                      )}
                    </span>
                  </button>
                  <button
                    className="ws-go"
                    aria-expanded={openId === j.id}
                    aria-label={`More for ${who(j)}`}
                    onClick={() => setOpenId(openId === j.id ? null : j.id)}
                  >
                    {openId === j.id ? <IconChevron size={16} /> : <IconMore size={16} />}
                  </button>
                </div>

                {/*
                  NO MARK AS PAID HERE.

                  Recording money means saying how much arrived, when, and by
                  what method, and lib/paper.ts keeps a list of exactly that
                  because part payments are ordinary. A button on a list row
                  could only write a single flag, which is the model this
                  product deliberately left behind. So the row opens the job,
                  where the payment gets entered properly.
                */}
                {openId === j.id && (
                  <div className="ws-rowacts">
                    <button className="btn" onClick={() => onOpenJob(j)}>
                      Open the job to record a payment
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <NotKnown title="What this list does not know" items={NOT_TRACKED} />
    </>
  )
}
